import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import CourseImage from "../../assets/svg/Landing/AllCourseImage.svg";
import CourseImageMobile from "../../assets/svg/Landing/AllCourseImageMobile.svg";
import Loader from "../../components/Live/Loader";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { API } from "../../shared/api/Api";
import CourseCard from "../../components/Landingpage/CourseCard";
import Dropdown from "../../components/reusable/Dropdown";
import CourseDetailsModal from "../../components/Landingpage/CourseDetailsModal";

const DEBOUNCE_DELAY = 700;

const AllCoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingCourseDetails, setLoadingCourseDetails] = useState(false);

  const fetchCourses = async (page = 1) => {
    setLoading(true);
    try {
      const { data } = await axios.get(API.Exposed.allCourses, {
        params: {
          page,
          limit: 20,
          search: debouncedSearch || undefined,
          category: selectedCategory || undefined,
        },
      });

      if (data.status === 200) {
        setCourses(data.data.items);
        setPagination({
          currentPage: data.data.current_page,
          lastPage: data.data.last_page,
          total: data.data.total,
        });
      } else {
        setError("Failed to load courses. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      setError("An error occurred while fetching courses. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(API.Exposed.allCategories);
      if (data.statusCode === 200) {
        setCategories(data.data.items);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchCourses();
    fetchCategories();
  }, [selectedCategory, debouncedSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination({
        currentPage: 1,
      });
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handlePageChange = (page) => {
    fetchCourses(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCourses(1);
  };

  const handleCategorySelect = useCallback(
    (categoryId) => {
      // If the same category is selected again, clear the filter
      if (selectedCategory === categoryId) {
        setSelectedCategory(null);
      } else {
        setSelectedCategory(categoryId);
      }
    },
    [selectedCategory]
  );

  const clearFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setSelectedCategory(null);
    setOpenDropdown(null);
    fetchCourses(1);
  };

  const toggleDropdown = (id) => {
    setOpenDropdown(openDropdown === id ? null : id);
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const handleCourseSelect = async (courseId) => {
    setLoadingCourseDetails(true);
    try {
      const { data } = await axios.get(
        `${API.Exposed.courseDetails}/${courseId}`
      );
      console.log(data);
      if (data.status === 200) {
        setSelectedCourse(data.data);
        setIsModalOpen(true);
      } else {
        console.error("Failed to load course details");
      }
    } catch (error) {
      console.error("Error fetching course details:", error);
    } finally {
      setLoadingCourseDetails(false);
    }
  };

  // Add function to close modal
  const closeModal = () => {
    setIsModalOpen(false);
    // Optional: Clear selected course after animation completes
    setTimeout(() => setSelectedCourse(null), 300);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header with opacity overlay */}
      <div
        className="relative text-white py-20 px-4"
        style={{
          height: "600px", // Fixed height
          overflow: "hidden",
        }}
      >
        {/* Background image with overlay - Fixed z-index */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage:
              window.screen.width > 1024
                ? `url(${CourseImage})`
                : `url(${CourseImageMobile})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            filter: "brightness(0.7)",
          }}
        >
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>

        {/* Content positioned over the image - Higher z-index */}
        <div className="container mx-auto relative z-20 h-full flex flex-col justify-center">
          <Link
            to="/"
            className="inline-flex items-center text-blue-100 hover:text-white mb-6"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Back to Home
          </Link>
          <motion.h1
            className="text-4xl md:text-5xl font-bold mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Explore Our Courses
          </motion.h1>
          <motion.p
            className="text-xl text-blue-100 max-w-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Discover a wide range of language courses designed to help you
            achieve fluency and cultural understanding.
          </motion.p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Search and Filter - Updated to use Dropdown */}
        <motion.div
          className="bg-white rounded-lg shadow-md p-6 mb-8"
          variants={fadeIn}
          initial="hidden"
          animate="visible"
        >
          <form
            onSubmit={handleSearch}
            className="flex flex-col md:flex-row gap-4"
          >
            <div className="flex-1">
              <label
                htmlFor="search"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Search Courses
              </label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by course name or description..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
            </div>

            <div className="md:w-1/4">
              <label
                htmlFor="category-dropdown"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Category
              </label>
              <Dropdown
                id="category-dropdown"
                title={
                  selectedCategory
                    ? categories.find((cat) => cat.id === selectedCategory)
                        ?.title || "Select Category"
                    : "Select Category"
                }
                items={categories}
                onItemSelect={handleCategorySelect}
                isOpen={openDropdown === "category-dropdown"}
                onToggle={toggleDropdown}
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                type="submit"
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition duration-300"
              >
                Search
              </button>

              <button
                type="button"
                onClick={clearFilters}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300"
              >
                Clear
              </button>
            </div>
          </form>
        </motion.div>

        {/* Results Summary */}
        <motion.div
          className="flex justify-between items-center mb-6"
          variants={fadeIn}
          initial="hidden"
          animate="visible"
        >
          <h2 className="text-xl font-semibold text-gray-800">
            {pagination.total} {pagination.total === 1 ? "Course" : "Courses"}{" "}
            Found
          </h2>

          <div className="text-sm text-gray-500">
            Page {pagination.currentPage} of {pagination.lastPage}
          </div>
        </motion.div>

        {/* Courses Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-600 p-4 rounded-md text-center">
            {error}
          </div>
        ) : (
          <AnimatePresence>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {courses.length > 0 ? (
                courses.map((course) => (
                  <motion.div
                    key={course.id}
                    variants={fadeIn}
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                    className="h-full"
                  >
                    <CourseCard
                      title={course.title}
                      description={course.description}
                      imageUrl={course.image_url}
                      price={course.price}
                      duration={course.duration}
                      level={course.level}
                      instructor={course.instructor_name}
                      category={course.category?.title}
                      placementTest={course.placement_test}
                      courseId={course.id}
                      onViewCourse={() => handleCourseSelect(course.id)}
                    />
                  </motion.div>
                ))
              ) : (
                <div className="col-span-full text-center py-12 text-gray-500">
                  No courses found matching your criteria. Try adjusting your
                  filters.
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}

        {/* Pagination */}
        {pagination.lastPage > 1 && (
          <motion.div
            className="flex justify-center mt-12"
            variants={fadeIn}
            initial="hidden"
            animate="visible"
          >
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className={`px-4 py-2 rounded-md ${
                  pagination.currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                } border border-gray-300`}
              >
                Previous
              </button>

              {[...Array(pagination.lastPage)].map((_, index) => {
                const page = index + 1;
                // Show current page, first page, last page, and pages around current
                if (
                  page === 1 ||
                  page === pagination.lastPage ||
                  (page >= pagination.currentPage - 1 &&
                    page <= pagination.currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-4 py-2 rounded-md ${
                        pagination.currentPage === page
                          ? "bg-primary text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      } border border-gray-300`}
                    >
                      {page}
                    </button>
                  );
                } else if (
                  page === pagination.currentPage - 2 ||
                  page === pagination.currentPage + 2
                ) {
                  return (
                    <span key={page} className="px-2">
                      ...
                    </span>
                  );
                }
                return null;
              })}

              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.lastPage}
                className={`px-4 py-2 rounded-md ${
                  pagination.currentPage === pagination.lastPage
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                } border border-gray-300`}
              >
                Next
              </button>
            </div>
          </motion.div>
        )}

        {/* Course Details Modal */}
        <AnimatePresence>
          {isModalOpen && selectedCourse && (
            <CourseDetailsModal
              course={selectedCourse}
              isOpen={isModalOpen}
              onClose={closeModal}
              loading={loadingCourseDetails}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AllCoursesPage;
